/**
 * Данные, взятые из карточки сделки (ТЗ FR-06).
 *
 * Новое дело предзаполняется само — вводить второй раз то, что менеджер уже
 * внёс в чек-лист, бессмысленно. Но подставленное надо показать: за то, что
 * попадёт в заявление, отвечает юрист, а не менеджер. Отсюда две задачи
 * у этой полосы — сказать, что подставлено, и дать посмотреть источник
 * каждого значения.
 *
 * Для дела, которое уже заполнялось, предзаполнения не происходит: там
 * предлагается кнопка, и решает юрист.
 */

import { useEffect, useState } from 'react';
import type { CaseProfile } from '@bd/domain';
import { applyToProfile, readCrmSnapshot, type CrmSnapshot, type FoundValue } from '../../storage/crm-import.js';

function SourceTable({ snapshot }: { snapshot: CrmSnapshot }) {
  return (
    <table className="simple-table">
      <thead>
        <tr>
          <th>Поле сделки</th>
          <th>Куда попало</th>
          <th>Значение</th>
        </tr>
      </thead>
      <tbody>
        {snapshot.found.map((value) => (
          <tr key={value.target}>
            <td>{value.sourceLabel}</td>
            <td>{value.title}</td>
            <td>{value.value}</td>
          </tr>
        ))}
        {snapshot.skipped.map((value, i) => (
          <tr key={`skip-${i}`} className="archived">
            <td>{value.sourceLabel}</td>
            <td colSpan={2}>не переносится: {value.reason}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CrmImportBanner({
  entityTypeId,
  caseId,
  item,
  profile,
  readOnly,
  prefilled,
  onApply,
}: {
  entityTypeId: number;
  caseId: string;
  /** Карточка дела: из неё берётся связанная сделка */
  item: Record<string, unknown> | null;
  profile: CaseProfile;
  readOnly: boolean;
  /** Что подставлено автоматически при открытии нового дела */
  prefilled: FoundValue[];
  onApply: (profile: CaseProfile) => void;
}) {
  const [snapshot, setSnapshot] = useState<CrmSnapshot | null>(null);
  const [applied, setApplied] = useState<FoundValue[] | null>(null);
  const [details, setDetails] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void readCrmSnapshot(entityTypeId, caseId, item).then((result) => {
      if (!cancelled) setSnapshot(result);
    });
    return () => {
      cancelled = true;
    };
  }, [entityTypeId, caseId, item]);

  if (!snapshot || snapshot.found.length === 0) return null;

  const shown = applied ?? (prefilled.length > 0 ? prefilled : null);

  if (shown) {
    return (
      <div className="banner banner-hint">
        <strong>Из карточки сделки подставлено: </strong>
        {shown.map((value) => value.title).join(', ')}.{' '}
        <button type="button" className="link-button" onClick={() => setDetails((open) => !open)}>
          {details ? 'Скрыть' : 'Проверить значения'}
        </button>
        <span className="hint">
          Проверьте их: чек-лист заполняет менеджер, а за содержание заявления отвечаете вы.
          Всё подставленное можно исправить в блоках дела как обычно.
        </span>
        {details && <SourceTable snapshot={snapshot} />}
      </div>
    );
  }

  const apply = () => {
    const result = applyToProfile(profile, snapshot.found);
    onApply(result.profile);
    setApplied(result.filled.length > 0 ? result.filled : []);
  };

  // Дело уже заполнялось: подставлять молча нельзя, но предложить стоит —
  // поля могли добавить в сделку уже после начала работы
  return (
    <div className="banner banner-hint">
      В карточке сделки есть данные, которых нет в деле:{' '}
      {snapshot.found.map((value) => value.title).join(', ')}.
      <button type="button" className="link-button" disabled={readOnly} onClick={apply}>
        Перенести
      </button>
      <button type="button" className="link-button" onClick={() => setDetails((open) => !open)}>
        {details ? 'Скрыть' : 'Что именно'}
      </button>
      {details && <SourceTable snapshot={snapshot} />}
      <span className="hint">Заполнятся только пустые поля — введённое вами не перезапишется.</span>
    </div>
  );
}
